import * as React from "react";
import { useEffect, useState } from "react";
import { LinearProgress } from "@material-ui/core";
import { useAsync } from "react-use";
import { IPictureModelDTO } from "groopit-js/client";
//
// A Picture either has been added by the user (a File) or is a string (really a Guid)
// which represents a picture that has been uploaded to the server and is now being edited
//
type Picture = File | IPictureModelDTO;

interface PreviewProps {
  file: File;
  inputPrefix: string;
}

const PicturePreview = (props: PreviewProps) => {
  const { file, inputPrefix } = { ...props };
  const [url, setUrl] = useState<string>();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    //
    // disable the submit button until the pictures is fully uploaded
    //
    const submit = document.querySelector("input.btn-progress");
    if (submit) {
      const refcount = parseInt(submit.getAttribute("disabled") ?? "0");
      submit.setAttribute("disabled", (refcount + 1).toString());
      setUrl(URL.createObjectURL(file));
      return () => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      };
    }
  }, [file]);

  const pending = useAsync(async () => {
    const resize_module = import("image-blob-reduce");
    const azure_module = import("@azure/storage-blob");
    const resize = (await resize_module).default();
    const shrunkPic = await resize.toBlob(file, { max: 1024 });
    const m = await import("groopit-js");
    const client = new m.GroopItApiClient({});
    const { upload, image } = await client.createPendingUpload(false);
    const azure = await azure_module;
    const uploader = new azure.BlockBlobClient(upload);
    await uploader.uploadData(shrunkPic, {
      onProgress: (e) => {
        setProgress((99 * e.loadedBytes) / file.size);
      },
      blobHTTPHeaders: { blobContentType: file.type },
    });
    setProgress(99);
    //
    // Load the resulting blob to see what the dimensions are
    //
    const img = new Image();
    img.src = URL.createObjectURL(shrunkPic);
    await img.decode();
    const { width, height } = img;
    URL.revokeObjectURL(img.src);

    //
    // re-enable the submit button
    //
    const submit = document.querySelector("input.btn-progress");
    if (submit) {
      const refcount = parseInt(submit.getAttribute("disabled") ?? "0");
      if (refcount !== 1) {
        submit.setAttribute("disabled", (refcount - 1).toString());
      } else {
        submit.removeAttribute("disabled");
      }
    }
    return { image, width, height };
  }, [file]);

  return (
    <>
      <img
        title="preview"
        className={`picture-component ${pending.loading ? "opacity-50" : ""}`}
        src={url}
      />
      {pending.loading || !pending.value ? (
        <LinearProgress variant="determinate" value={progress} />
      ) : (
        <>
          <input
            type="hidden"
            name={`${inputPrefix}Image`}
            value={pending.value.image}
          />
          <input
            type="hidden"
            name={`${inputPrefix}Width`}
            value={pending.value.width}
          />
          <input
            type="hidden"
            name={`${inputPrefix}Height`}
            value={pending.value.height}
          />
        </>
      )}
    </>
  );
};

interface ExistingImageProps {
  picture: IPictureModelDTO;
  inputPrefix: string;
  pictureImageBase: string;
}

const ExistingImage = (props: ExistingImageProps) => {
  const { picture, inputPrefix, pictureImageBase } = {
    ...props,
  };

  return (
    <>
      <img
        title="preview"
        className="picture-component"
        src={`${pictureImageBase}${picture.image}`}
      />
      <input type="hidden" name={`${inputPrefix}Image`} value={picture.image} />
      <input type="hidden" name={`${inputPrefix}Width`} value={picture.width} />
      <input
        type="hidden"
        name={`${inputPrefix}Height`}
        value={picture.height}
      />
    </>
  );
};

interface PictureWrapperProps {
  picture: Picture;
  sequence: number;
  inputPrefix: string;
  pictureImageBase: string;
  onRemove: () => void;
}

//
// common drag and drop wrapper and chrome for that handles both
// existing pictures and new pictures
//
const PictureWrapper = (props: PictureWrapperProps) => {
  const { picture, sequence, inputPrefix, onRemove, pictureImageBase } = {
    ...props,
  };

  return (
    <div>
      <div className="flex flex-row-reverse">
        <button
          type="button"
          title="remove image"
          className="m-2"
          onClick={onRemove}
        >
          <img
            title="Remove"
            src="/Content/Images/delete.png"
            className="w-4"
          />
        </button>
      </div>
      {picture instanceof File ? (
        <PicturePreview
          // if we just use the index as they key, then deleting or rearranging
          // the files will break the key and force a re-uploade
          file={picture}
          inputPrefix={`${inputPrefix}[${sequence}].`}
        />
      ) : (
        <ExistingImage
          picture={picture}
          inputPrefix={`${inputPrefix}[${sequence}].`}
          pictureImageBase={pictureImageBase}
        />
      )}
    </div>
  );
};

interface PictureProps {
  description: string;
  inputPrefix: string;
  picture: IPictureModelDTO;
  pictureImageBase: string;
  disabled: boolean;
}

export const Picture = (props: PictureProps): JSX.Element => {
  const [file, setFile] = React.useState<Picture | null | undefined>(
    props.picture,
  );

  return (
    <>
      <label className="text-display-normal text-color-secondary w-full">
        <div className="flex items-center space-x-3 point-on-hover">
          <span className="flex-grow">{props.description}</span>
          {file ? (
            <button
              type="button"
              title="remove image"
              className="m-2"
              onClick={(event) => {
                setFile(undefined);
                event.preventDefault();
              }}
            >
              <img
                title="Remove"
                src="/Content/Images/delete.png"
                className="w-4"
              />
            </button>
          ) : (
            <img
              title="Add Picture"
              src="/Content/Images/add.png"
              className={`w-4 ${
                props.disabled
                  ? "opacity-60 cursor-not-allowed"
                  : "point-on-hover"
              }`}
            />
          )}
        </div>
        <input
          title="Add Picture"
          type="file"
          className="hidden"
          disabled={props.disabled}
          accept="image/jpeg,image/png,image/gif"
          onChange={(e) => {
            setFile(e.target.files?.item(0));
            e.target.value = "";
          }}
        />
      </label>
      {file && file instanceof File ? (
        <PicturePreview file={file} inputPrefix={`${props.inputPrefix}.`} />
      ) : file ? (
        <ExistingImage
          picture={file}
          inputPrefix={`${props.inputPrefix}.`}
          pictureImageBase={props.pictureImageBase}
        />
      ) : (
        <></>
      )}
    </>
  );
};

interface PictureListProps {
  description: string;
  inputPrefix: string;
  pictures: IPictureModelDTO[];
  pictureImageBase: string;
  disabled: boolean;
}

export const PictureList = (props: PictureListProps): JSX.Element => {
  const { description, pictures } = { ...props };
  const [files, setFiles] = useState<Picture[]>(pictures);

  return (
    <>
      <label className="text-display-normal text-color-secondary w-full">
        <div className="flex items-center space-x-3 point-on-hover">
          <span className="flex-grow ">{description}</span>
          <img
            title="Add Pictures"
            src="/Content/Images/add.png"
            className={`w-4 ${
              props.disabled
                ? "opacity-60 cursor-not-allowed"
                : "point-on-hover"
            }`}
          />
          <input
            title="Add Picture"
            type="file"
            className="hidden"
            disabled={props.disabled}
            accept="image/jpeg,image/png,image/gif"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              setFiles((old) => old.concat(files));
              e.target.value = "";
            }}
          />
        </div>
      </label>
      <div>
        {files.map((p, i) => (
          <PictureWrapper
            key={p instanceof File ? p.name : p.image}
            picture={p}
            sequence={i}
            onRemove={() => {
              setFiles(files.filter((f) => f !== p));
            }}
            {...props}
          />
        ))}
      </div>
    </>
  );
};
